// The recipe's cost / margin summary: batch cost, cost per oz and, for a
// product, ingredient cost, fixed costs, product cost and margins (retail and
// wholesale). Shown under the Recipe dialog's lines, and on the product
// dialog's Pricing & Stock tab.

import { AlertTriangle } from 'lucide-react'
import type { RecipeEditor } from './recipeEditor'
import { WHOLESALE_DISCOUNTS, marginAt, wholesalePrice } from '../../utils/margins'

const money = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

const marginClass = (m: number | null | undefined) =>
  m != null
    ? m >= 0
      ? 'tabular-nums font-medium text-emerald-600 dark:text-emerald-500'
      : 'tabular-nums font-medium text-destructive'
    : 'tabular-nums'

export default function RecipeCostSummary({ editor }: { editor: RecipeEditor }) {
  const {
    product, recipeOnly, byPercent, batchCost, costPerOz, actualWeightOz, weightOz,
    price, ingredientCost, fixedTotal, productCost, margin, marginPct,
    missingReasons, incomplete,
  } = editor
  return (
    <div className="rounded-md border bg-muted/40 p-3 text-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">
          {recipeOnly ? 'Cost' : `Cost for ${product?.item_name ?? product?.sku}`}
        </span>

        {incomplete ? (
          <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
            <AlertTriangle className="h-3.5 w-3.5" />
            Estimate — missing data
          </span>
        ) : null}
      </div>

      {incomplete ? (
        <ul className="mb-2 list-disc space-y-0.5 pl-5 text-xs text-amber-700 dark:text-amber-500">
          {missingReasons.map(r => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ) : null}

      <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">{byPercent ? 'Cost / 100 oz' : 'Batch cost'}</dt>
          <dd className="tabular-nums">{money(batchCost)}</dd>
        </div>

        <div className="flex justify-between">
          <dt className="text-muted-foreground">Cost / oz</dt>
          <dd className="tabular-nums">{costPerOz != null ? money(costPerOz) : '—'}</dd>
        </div>

        {recipeOnly ? null : (
          <>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                Ingredient cost
                {weightOz != null
                  ? ` (${weightOz} oz${actualWeightOz != null ? ' actual' : ''})`
                  : ''}
              </dt>
              <dd className="tabular-nums">
                {ingredientCost != null ? money(ingredientCost) : '—'}
              </dd>
            </div>

            <div className="flex justify-between">
              <dt className="text-muted-foreground">Fixed costs (bags, labels)</dt>
              <dd className="tabular-nums">{money(fixedTotal)}</dd>
            </div>

            <div className="flex justify-between">
              <dt className="text-muted-foreground">Product cost</dt>
              <dd className="tabular-nums font-medium">
                {productCost != null ? money(productCost) : '—'}
              </dd>
            </div>

            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                Margin{price != null ? ` (@ ${money(price)})` : ''}
              </dt>
              <dd className={marginClass(margin)}>
                {margin != null ? money(margin) : '—'}
                {marginPct != null ? (
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({marginPct.toFixed(0)}%)
                  </span>
                ) : null}
              </dd>
            </div>

            {WHOLESALE_DISCOUNTS.map(d => {
              const at = price != null ? wholesalePrice(price, d) : null
              const m = marginAt(at, productCost)
              return (
                <div key={d} className="flex justify-between">
                  <dt className="text-muted-foreground">
                    Margin @ {Math.round(d * 100)}% off
                    {at != null ? ` (${money(at)})` : ''}
                  </dt>
                  <dd className={marginClass(m?.margin)}>
                    {m != null ? money(m.margin) : '—'}
                    {m?.pct != null ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({(m.pct * 100).toFixed(0)}%)
                      </span>
                    ) : null}
                  </dd>
                </div>
              )
            })}
          </>
        )}
      </dl>
    </div>
  )
}
